import {compactObj, entries} from 'shared/collection'
import {Permission} from './permission'
import {env} from '../../utils/env'
import {Uri, uri} from 'shared/uri'
import {z, type ZodObject, type ZodRawShape} from 'zod'
import {zx} from 'shared/type'
import {zPortalQuery} from './portalTypes'

export type MenuPermission = Permission | 'global-admin'

type NavCfgItemMenu = {
  type: 'menu'
  title?: string
  isHome?: boolean
  permissions?: MenuPermission[]
  subItems?: Record<
    string,
    {
      title: string
      description: string
      category?: string
      permissions?: MenuPermission[]
    }
  >
  getUrl: (params: any, options?: {external?: boolean}) => Uri
}

type NavCfgItem = {} & (
  | NavCfgItemMenu
  | {
      type: 'link'
      getUrl: (params: any, options: {external?: boolean}) => Uri
    }
  | {
      type: 'link-static'
      url: Uri
    }
)

const zWs = z.object({
  workspace: z.string()
})

const adminSubItems = {
  users: {
    title: 'Users',
    description: 'Manage users, roles and permissions'
  },
  workspace: {
    title: 'Workspace',
    description: 'Configure workspace settings and features'
  },
  subscription: {
    title: 'Subscription',
    description: 'Manage workspace subscription and billing'
  },
  'forecaster-participation': {
    title: 'Forecaster Participation',
    description: 'Track and manage forecaster activity'
  },
  'global-subscriptions': {
    title: 'Subscriptions',
    description: 'Manage all subscriptions',
    category: 'Global',
    permissions: ['global-admin'] as MenuPermission[]
  }
} as const

/** create a function to get a url for a navigation item */
const makeGetUrl =
  <T extends ZodRawShape>(
    basePath: string,
    shape?: T,
    transform?: (url: Uri<T>, params: z.infer<ZodObject<T>>) => Uri<T>
  ) =>
  (params: z.infer<ZodObject<T>>, options?: {external?: boolean}) => {
    const url = uri(options?.external ? env.URL : '/', shape ? z.object(shape) : undefined).addPath(basePath)
    return transform ? transform(url, params) : url.mergeQuery(compactObj(params) as any)
  }

export const nav = {
  questions: {
    type: 'menu',
    title: 'Questions',
    isHome: true,
    permissions: ['question-view'],
    getUrl: makeGetUrl('', {
      ...zWs.shape,
      ...zPortalQuery.shape
    })
  },
  admin: {
    type: 'menu',
    title: 'Admin',
    permissions: ['settings'],
    subItems: adminSubItems,
    getUrl: makeGetUrl('admin', {
      ...zWs.shape,
      tab: zx.enumFromObjKeys(adminSubItems),
      mode: z.string().optional(),
      session_id: z.string().optional(),
      adminSections: z.string().optional()
    })
  },
  feedback: {
    type: 'menu',
    title: 'Feedback',
    getUrl: makeGetUrl('feedback', zWs.shape)
  },
  accuracy: {
    type: 'menu',
    title: 'Accuracy',
    getUrl: makeGetUrl('accuracy', zWs.shape)
  },
  question: {
    type: 'link',
    getUrl: makeGetUrl(
      'question',
      {
        question_id: z.string(),
        subquestion_id: z.string().optional(),
        comment_id: z.string().optional(),
        workspace: z.string().optional()
      },
      (url, {question_id, subquestion_id, ...params}) =>
        url.addPath(question_id, subquestion_id).mergeQuery(compactObj(params))
    )
  },
  help: {
    type: 'link-static',
    url: uri('https://swiftcentre.notion.site/')
  },
  helpAsset: {
    type: 'link-static',
    url: uri(env.NEXT_PUBLIC_HELP_ASSET_URL)
  },
  contactUs: {
    type: 'link-static',
    url: uri('https://www.swiftcentre.org/contact-us')
  }
} satisfies Record<string, NavCfgItem>

const isNavCfgItemMenu = (v: NavCfgItem): v is NavCfgItemMenu => v.type == 'menu'

export const menuItems = entries(nav)
  .filter(([_, v]) => isNavCfgItemMenu(v))
  .map(([name, v]) => ({
    ...(v as NavCfgItemMenu),
    name
  }))
